import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import {
  type EnemyState,
  type EnemyStateContext,
  StatefulEnemyRuntime,
} from "../enemy-system"

const SCOUT_APPROACH_SPEED = 4.8
const SCOUT_FEINT_X = GAME_WIDTH * 0.58
const SCOUT_FEINT_MS = 420
const SCOUT_DASH_SPEED = 8.5
const SCOUT_EXIT_SPEED = 4.2
const SCOUT_TRACK_SPEED = 0.12

type ScoutPhase = "approach" | "feint" | "dash" | "exit"

export class ScoutEnemy extends StatefulEnemyRuntime<ScoutPhase> {
  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<ScoutPhase>[] = [
      {
        name: "approach",
        behaviorState: "tracking",
        animationState: "flying",
        enter: (context) => this.playScoutAnimation(context),
        update: (context) => this.updateApproach(context),
      },
      {
        name: "feint",
        behaviorState: "focusing",
        animationState: "focusing",
        canFire: false,
        enter: (context) => {
          context.sprite.setTint(0xf472b6)
          context.vfx.emitImpactBurst(
            context.sprite.x,
            context.sprite.y,
            0xf472b6,
            0.55,
          )
        },
        update: (context) => this.updateFeint(context),
      },
      {
        name: "dash",
        behaviorState: "attacking",
        animationState: "attacking",
        canFire: false,
        enter: (context) => {
          context.sprite.clearTint()
          context.enemyData.vx = context.scrollCompensation - SCOUT_DASH_SPEED
          context.enemyData.vy = Phaser.Math.Clamp(
            (context.playerY - context.sprite.y) * 0.08,
            -2.8,
            2.8,
          )
        },
        update: (context) => this.updateDash(context),
      },
      {
        name: "exit",
        behaviorState: "retreating",
        animationState: "retreating",
        canFire: false,
        update: (context) => this.updateExit(context),
      },
    ]

    super(sprite, enemyData, "approach", states)
  }

  public override onHeroCollision():
    | "damage-only"
    | "damage-and-defeat"
    | null {
    return "damage-and-defeat"
  }

  private playScoutAnimation(context: EnemyStateContext<ScoutPhase>): void {
    if (context.scene.anims.exists("anim_scout")) {
      context.sprite.play("anim_scout", true)
    }
  }

  private updateApproach(
    context: EnemyStateContext<ScoutPhase>,
  ): ScoutPhase | null {
    context.enemyData.vx = context.scrollCompensation - SCOUT_APPROACH_SPEED
    context.enemyData.vy = Phaser.Math.Clamp(
      (context.playerY - context.sprite.y) * SCOUT_TRACK_SPEED,
      -2.4,
      2.4,
    )

    if (context.sprite.x <= SCOUT_FEINT_X) {
      return "feint"
    }

    return null
  }

  private updateFeint(
    context: EnemyStateContext<ScoutPhase>,
  ): ScoutPhase | null {
    context.enemyData.vx = context.scrollCompensation - 0.55
    context.enemyData.vy =
      Math.sin(context.time / 80) * 1.6 +
      Phaser.Math.Clamp((context.playerY - context.sprite.y) * 0.035, -1, 1)

    if (context.stateTime >= SCOUT_FEINT_MS) {
      return "dash"
    }

    return null
  }

  private updateDash(
    context: EnemyStateContext<ScoutPhase>,
  ): ScoutPhase | null {
    if (context.sprite.x < 36) {
      return "exit"
    }

    return null
  }

  private updateExit(
    context: EnemyStateContext<ScoutPhase>,
  ): ScoutPhase | null {
    const targetY = Phaser.Math.Clamp(
      context.sprite.y + (context.sprite.y < GAME_HEIGHT * 0.5 ? -90 : 90),
      40,
      GAME_HEIGHT - 80,
    )
    context.enemyData.vx = context.scrollCompensation + SCOUT_EXIT_SPEED
    context.enemyData.vy = (targetY - context.sprite.y) * 0.06

    if (context.sprite.x > GAME_WIDTH + 80) {
      context.sprite.destroy()
    }

    return null
  }
}
